/* eslint-disable no-underscore-dangle */
import { schema } from 'normalizr';
import merge from 'lodash/merge';
import {
  LocalizableString, LocalizableImage,
  LocalizableVideo, LocalizableFile,
} from '../common/types';
import { BoothTaxonomy } from '../boothTaxonomies';
import { mapLocalizable } from '../common/utils';
import { boothTabEntity } from '../boothTabs/types';

export enum DesignElementKind {
  Image = 'IMAGE',
  Richtext = 'RICHTEXT',
  Color = 'COLOR',
  Video = 'VIDEO',
  PDF = 'PDF',
}

export interface DesignElement {
  id: string;
  name: string;
  kind: DesignElementKind;
  color?: string;
  image?: LocalizableImage;
  video?: LocalizableVideo;
  contentRichtextHtml?: LocalizableString;
  thumbnailImage?: LocalizableImage;
  pdf?: LocalizableFile;
  actionUrl?: string;
}

export interface DesignCanvasLayer {
  id: string;
  name: string;
  image?: LocalizableImage;
  contentRichtextHtml?: LocalizableString;
  video?: LocalizableVideo;
  pdf?: LocalizableFile;
  thumbnailImage?: LocalizableImage;
  kind?: string;
  fillColor?: string;
  maskImage?: LocalizableImage;
  normalStateImage?: LocalizableImage;
  hoverStateImage?: LocalizableImage;
  actionType?: string;
  actionExternalLinkUrl?: string;
  actionUrl?: string;
  __typename: string;
  width: string;
  height: string;
  positionX: string;
  positionY: string;
  keyName?: string;
}

export interface DesginMeasurement {
  dimension: number;
  unit: string;
}

export interface BoothDesignCanvas {
  id: string;
  width: string;
  height: string;
  positionX: string;
  positionY: string;
  aspectRatio: string;
  maxWidth: string;
  layers: Array<DesignCanvasLayer>;
  previewImage: {
    originalImageUrl: string;
  };
}

export enum BoothFetchingState {
  Pending = 'PENDING',
  Fulfilled = 'FULFILLED',
  Rejected = 'REJECTED',
}

export enum BoothFetchedLevel {
  Meta = 'META',
  Design = 'DESIGN',
  Details = 'DETAILS',
}

export const BoothFetchedLevelOrder = [
  BoothFetchedLevel.Meta,
  BoothFetchedLevel.Design,
  BoothFetchedLevel.Details,
];

export const isFetchingLevelLowerThanExpected = (currentLevel: BoothFetchedLevel, expectedLevel: BoothFetchedLevel) => BoothFetchedLevelOrder.indexOf(currentLevel) < BoothFetchedLevelOrder.indexOf(expectedLevel);

export const isFetchingLevelHigherThanExpected = (currentLevel: BoothFetchedLevel, expectedLevel: BoothFetchedLevel) => BoothFetchedLevelOrder.indexOf(currentLevel) > BoothFetchedLevelOrder.indexOf(expectedLevel);
export interface Booth {
  salesImg: any;
  id: string;
  appliedDesignCodename: string;
  appointmentBookingUrl: string;
  meetupEnabled: boolean;
  website: string;
  boothNumber: string;
  description: LocalizableString;
  name: LocalizableString;
  logo: LocalizableImage;
  tier: {
    id: string;
  };
  designElements: DesignElement[];
  categories: BoothTaxonomy[];
  rootContentElement: {
    tabs: string[];
  };
  hasAttachment: boolean;
  attachmentTab: number;
  livestreams: any;
  videoCallUrl: string;
  rowOrder: string;
  boothDesign: {
    id: string;
  };
  boothDesignCanvas: BoothDesignCanvas;

  $lastReceivedAt: string;
  $fetchingState: BoothFetchingState;
  $fetchedLevel: {
    level: BoothFetchedLevel;
    locales: string[];
  }[];
}

const convertMesurement = (measurement: DesginMeasurement): string => {
  const { dimension, unit } = measurement || {};
  switch (unit) {
    case 'PIXEL':
      return `${dimension}px`;
    case 'PERCENT':
      return `${dimension}%`;
    default:
      return '';
  }
};

const addExtraFieldsToEntity = (source) => {
  const entity = { ...source };

  const hasPreviewImageKey = 'previewImage' in (entity.boothDesignCanvas || {});

  if (!hasPreviewImageKey && entity.boothDesign?.id && entity.boothDesignCanvas && !entity.boothDesignCanvas.maxWidth) {
    // eslint-disable-next-line no-throw-literal
    throw 'cache error!';
  }
  if (typeof entity?.boothDesignCanvas?.maxWidth === 'object') {
    const {
      maxWidth, aspectRatio, positionX, positionY, height, width,
      layers: { nodes },
    } = entity.boothDesignCanvas;
    entity.boothDesignCanvas.maxWidth = convertMesurement(maxWidth);
    entity.boothDesignCanvas.aspectRatio = `${(100.0 / aspectRatio?.dimension) * 100}%`;
    entity.boothDesignCanvas.positionX = convertMesurement(positionX);
    entity.boothDesignCanvas.positionY = convertMesurement(positionY);
    entity.boothDesignCanvas.height = convertMesurement(height);
    entity.boothDesignCanvas.width = convertMesurement(width);
    const complierLayers = nodes?.slice()?.reverse()?.map((layer) => {
      const newLayer = {
        ...layer,
        positionX: convertMesurement(layer?.positionX),
        positionY: convertMesurement(layer?.positionY),
        height: convertMesurement(layer?.height),
        width: convertMesurement(layer?.width),
      };
      return newLayer;
    });
    entity.boothDesignCanvas.layers = complierLayers;
  }
  return entity;
};

export const boothEntity = new schema.Entity(
  'booths',
  {
    rootContentElement: {
      tabs: [boothTabEntity],
    },
  },
  {
    mergeStrategy(first, second) {
      return merge(first, mapLocalizable(addExtraFieldsToEntity(second)));
    },
    processStrategy(entity) {
      return mapLocalizable(addExtraFieldsToEntity(entity));
    },
  },
);
